import React from 'react';

import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import Add from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import Permissions from 'react-redux-permissions';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';

import { Button } from '@fd/ui/atoms';
import PageLayout from '@fd/ui/Layout';
import LazyComponent from '@fd/ui/LazyComponent';
import Tabs from '@fd/ui/Tabs';

import { lazyWithRetry } from '../../helpers/utilities';
import { flagService } from '../../services/flagService';
import { RMSModules } from '../RMS/utils/rmsModules';
import { RMSSubscriptions } from '../RMS/utils/rmsSubscriptions';
import CustomerList from './List/CustomersList';

const CircularProgressContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
}));

const StyledButtonContent = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

const RMSPage = lazyWithRetry(() => import('../RMS/components/RMSPage'));

const CustomersPage = (props: MappedState) => {
  const { appId, translate, isImportCustomersEnabled } = props;
  console.log('[CustomersPage]', { isImportCustomersEnabled });
  const history = useHistory();
  const { isLoading, data } = useQueryRMSCommonDataHook(appId || '');
  const subscriptions = data?.subscriptions || [];

  const importButton = isImportCustomersEnabled ? (
    <Permissions allowed={['Owner', 'ManagedOwner', 'FlipdishStaff']}>
      <Button
        fdKey="Action-button-Import_Customers"
        onClick={() => history.push(`/${appId}/customers/import`)}
      >
        <StyledButtonContent>
          <Add />
          {translate('Import_Customers')}
        </StyledButtonContent>
      </Button>
    </Permissions>
  ) : null;

  const tabItems = [
    {
      label: translate('Main') as string,
      component: <CustomerList />,
      queryString: 'main',
    },
    {
      label: 'RMS',
      component: (
        <LazyComponent>
          <RMSPage
            pageLayoutEnabled={false}
            subscription={RMSSubscriptions.BASE}
            title={translate('Customers')}
            module={RMSModules.CUSTOMERS}
            url={`modules/marketing/${RMSModules.CUSTOMERS}`}
          />
        </LazyComponent>
      ),
      queryString: 'rms',
    },
  ];

  // edge case in which while loading RMS subscriptions the customers renders and overrites the url
  const oldCustomersPage = isLoading ? (
    <CircularProgressContainer>
      <CircularProgress />
    </CircularProgressContainer>
  ) : (
    <CustomerList />
  );

  return (
    <PageLayout
      auditLogsFilter={{ type: 'EventType', value: 'customer.*' }}
      documentTitle={'Customers'}
      title={translate('Customers')}
      contextButtons={[importButton]}
    >
      {subscriptions?.includes(RMSSubscriptions.BASE) ? (
        <Tabs tabItems={tabItems} />
      ) : (
        oldCustomersPage
      )}
    </PageLayout>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  return {
    appId: state?.currentApp?.AppId,
    isImportCustomersEnabled: flagService.isFlagOn(state, 'importCustomers'),
    translate: getTranslate(state.locale),
  };
};

export default connect(mapStateToProps)(CustomersPage);
